/* ============================================================
   OEE INPUT DISPLAY NORMALIZER V3
   Fix decimal flash + allow zero.
   Scope: OUTPUT / PRODUKSI, JAM1..JAM10
   ============================================================ */

const SIP_OEE_VERSION = "v3";
const sipOeeTouchedJam = new Set();

let sipOeeObserver = null;
let sipOeeScheduled = false;

function isOeePage() {
  return /\/produksi\/oee(?:\/|$)/i.test(window.location.pathname);
}

function normText(v) {
  return String(v || "").replace(/\s+/g, " ").trim();
}

function isZeroish(v) {
  const s = String(v ?? "").trim().replace(",", ".");
  return !!s && /^[-+]?0+(?:\.0+)?$/.test(s);
}

function trimDecimal(v) {
  const raw = String(v ?? "").trim();
  if (!raw) return "";

  const s = raw.replace(",", ".");

  // Biarkan input non numeric apa adanya.
  if (!/^[-+]?\d+(?:\.\d*)?$/.test(s)) {
    return raw;
  }

  // Saat masih "10." jangan ubah sampai blur.
  if (s.endsWith(".")) {
    return s;
  }

  if (!s.includes(".")) {
    return s;
  }

  return s
    .replace(/(\.\d*?[1-9])0+$/, "$1")
    .replace(/\.0+$/, "");
}

function findOutputRow() {
  return [...document.querySelectorAll("tr")].find((tr) => {
    const t = normText(tr.textContent).toUpperCase();

    return (
      t.includes("OUTPUT / PRODUKSI") ||
      t.includes("OUTPUT/PRODUKSI") ||
      t.includes("OUTPUT PRODUKSI")
    );
  }) || null;
}

function getJamInputs() {
  const row = findOutputRow();
  if (!row) return [];

  const inputs = [...row.querySelectorAll("input")].filter((input) => {
    const type = String(input.type || "text").toLowerCase();

    return ![
      "date",
      "datetime-local",
      "checkbox",
      "radio",
      "file",
      "hidden",
      "button",
      "submit",
    ].includes(type);
  });

  // 10 input paling kanan adalah JAM1..JAM10.
  return inputs.length >= 10 ? inputs.slice(-10) : inputs;
}

function jamIndex(input) {
  return getJamInputs().indexOf(input);
}

function isTarget(input) {
  return input instanceof HTMLInputElement && jamIndex(input) >= 0;
}

function nativeSet(input, value) {
  const d = Object.getOwnPropertyDescriptor(
    HTMLInputElement.prototype,
    "value"
  );

  if (d?.set) {
    d.set.call(input, value);
  } else {
    input.value = value;
  }
}

function normalizedDisplay(value, touched) {
  const current = String(value ?? "").trim();

  if (!current) {
    return "";
  }

  if (isZeroish(current)) {
    return touched ? "0" : "";
  }

  return trimDecimal(current);
}

function normalizeInput(input, index, force = false) {
  if (!input || !input.isConnected) return;

  // Field yang sedang diketik tidak boleh diubah.
  if (!force && document.activeElement === input) {
    return;
  }

  const current = String(input.value ?? "").trim();
  const touched = sipOeeTouchedJam.has(index);
  const next = normalizedDisplay(current, touched);

  if (next !== current) {
    nativeSet(input, next);
  }

  input.placeholder = "";
  input.dataset.sipOeeNormalizeVersion = SIP_OEE_VERSION;
}

function normalizeAllExceptActive() {
  if (!isOeePage()) return;

  const inputs = getJamInputs();

  inputs.forEach((input, index) => {
    normalizeInput(input, index, false);
  });

  document.body.dataset.sipOeeNormalizer = SIP_OEE_VERSION;
  document.body.dataset.sipOeeJamCount = String(inputs.length);
}

/*
 * Jalankan normalisasi secepat mungkin:
 * - microtask: setelah React event handler
 * - requestAnimationFrame: sebelum paint berikutnya
 *
 * Ini menghilangkan flash 0.00 / 1.00 pada kolom lain.
 */
function normalizeBeforePaint() {
  if (sipOeeScheduled) return;

  sipOeeScheduled = true;

  queueMicrotask(() => {
    try {
      normalizeAllExceptActive();
    } finally {
      requestAnimationFrame(() => {
        try {
          normalizeAllExceptActive();
        } finally {
          sipOeeScheduled = false;
        }
      });
    }
  });
}

function onInput(event) {
  const input = event.target;
  if (!isTarget(input)) return;

  const index = jamIndex(input);

  if (index >= 0) {
    sipOeeTouchedJam.add(index);
  }

  // Jangan ubah input aktif, hanya rapikan kolom lain sebelum paint.
  normalizeBeforePaint();
}

function onBlur(event) {
  const input = event.target;
  if (!isTarget(input)) return;

  const index = jamIndex(input);

  if (index >= 0) {
    sipOeeTouchedJam.add(index);
  }

  const current = String(input.value ?? "").trim();

  if (current !== "") {
    let next = isZeroish(current)
      ? "0"
      : trimDecimal(current).replace(/\.$/, "");

    if (next !== current) {
      nativeSet(input, next);

      input.dispatchEvent(
        new Event("input", { bubbles: true })
      );

      input.dispatchEvent(
        new Event("change", { bubbles: true })
      );
    }
  }

  normalizeBeforePaint();
}

function resetTouched() {
  sipOeeTouchedJam.clear();
  normalizeBeforePaint();
}

function onClick(event) {
  if (!isOeePage()) return;

  const el = event.target?.closest?.(
    "button,a,[role='tab']"
  );

  if (!el) return;

  const text = normText(el.textContent).toUpperCase();

  const triggers = new Set([
    "SEARCH",
    "CREATE",
    "RESET",
    "MACHINING",
    "MODELINE",
    "PH & SMS",
    "PRESS BLANK",
    "PRESS REG",
    "PRESS ML",
    "PRESS PROG",
    "SEAT LOCK COMP",
    "ST SHAFT",
    "SPOT",
  ]);

  if (triggers.has(text)) {
    setTimeout(resetTouched, 60);
  }
}

function start() {
  if (sipOeeObserver) return;

  document.addEventListener("input", onInput, true);
  document.addEventListener("blur", onBlur, true);
  document.addEventListener("click", onClick, true);

  sipOeeObserver = new MutationObserver(() => {
    normalizeBeforePaint();
  });

  sipOeeObserver.observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["value", "class"],
  });

  window.addEventListener("popstate", normalizeBeforePaint);

  [0, 100, 250, 500, 1000].forEach((ms) => {
    setTimeout(normalizeBeforePaint, ms);
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", start, { once: true });
} else {
  start();
}
